import { Component, ViewChild, ElementRef, AfterViewInit, Injectable } from '@angular/core';
import * as opentype from 'opentype.js';
import { FontService } from '../services/font.service';

interface FontDetails {
  name: string;
  displayName: string;
  displayNameKey: string;
  path: string;
  fontFamily: string;
}

// Add this interface to better type the font categories
interface FontCategory {
  name: string;
  nameKey: string;
  fonts: FontDetails[];
}

@Component({
  selector: 'app-write',
  templateUrl: './write.page.html',
  styleUrls: ['./write.page.scss'],
  providers: [FontService]
})
export class WritePage implements AfterViewInit {
  @ViewChild('myCanvas') canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('strokeVideo') strokeVideo!: ElementRef<HTMLVideoElement>;
  private ctx!: CanvasRenderingContext2D;
  private lastX!: number;
  private lastY!: number;
  private drawing = false;
  letter: string = 'அ';
  selectedMei: string = '';
  selectedUyir: string = '';
  uyirletters: string[] = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
  meiletters: string[] = ['க்', 'ச்', 'ட்', 'த்', 'ப்', 'ற்', 'ங்', 'ஞ்', 'ண்', 'ந்', 'ம்', 'ன்', 'ய்', 'ர்', 'ல்', 'வ்', 'ழ்', 'ள்'];
  uyirSigns: string[] = ['', 'ா', 'ி', 'ீ', 'ு', 'ூ', 'ெ', 'ே', 'ை', 'ொ', 'ோ', 'ௌ'];
  dynamicUyirMeiLetters: string[] = [];
  private letterBoundingBox: { x: number, y: number, width: number, height: number } | null = null;
  private font: opentype.Font | null = null;
  private letterPath: Path2D | null = null;
  private letterPixels: boolean[][] = [];
  private letterImageData: ImageData | null = null;
  romanTransliteration: string = '';
  showManual: boolean = false;
  WriteOrderVisible: boolean = false;
  currentCharacter: string = '';
  private videoLoaded = false;
  selectedDemoType: 'image' | 'video' = 'image';
  availableFonts: FontDetails[] = [];
  selectedFont: FontDetails | null = null;
  showFontSelect = false;
  showCredits = false;

  // Add new properties for categorized fonts
  categorizedFonts: FontCategory[] = [
    {
      name: 'Modern Scripts',
      nameKey: 'FONT.CATEGORY.MODERN',
      fonts: []
    },
    {
      name: 'Old Scripts',
      nameKey: 'FONT.CATEGORY.OLD',
      fonts: []
    }
  ];

  constructor(private fontService: FontService) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', event => {
      this.drawLetter();
    });
    this.loadFont();
    this.loadAvailableFonts();
  }

  private async loadFont() {
    if (!this.selectedFont) return;
    
    try {
      const loadedFont = await opentype.load(this.selectedFont.path);
      
      // Load the font into the browser
      this.loadFontStyle(this.selectedFont.path, this.selectedFont.fontFamily);
      await document.fonts.load(`16px "${this.selectedFont.fontFamily}"`);
      
      this.font = loadedFont;
      if (this.ctx) {
        await new Promise(resolve => setTimeout(resolve, 100));
        this.drawLetter();
      }
    } catch (error: any) {
      console.error('Font loading error:', error);
    }
  }

  isDarkMode(): boolean {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  isMeiLetter(letter: string): boolean {
    return this.meiletters.includes(letter);
  }

  ngAfterViewInit(): void {
    this.setupCanvas();
    this.drawLetter(); // Initial drawing of the default letter
    this.playAudioForChar('அ');
  }

  setupCanvas() {
    const canvasEl = this.canvas.nativeElement;
    this.ctx = canvasEl.getContext('2d')!;
    this.resizeCanvas();
    window.addEventListener('resize', this.resizeCanvas.bind(this));
    this.addEventListeners(canvasEl);
  }

  resizeCanvas() {
    const canvasEl = this.canvas.nativeElement;
    const ratio = window.devicePixelRatio || 1;
    const isLandscape = window.innerWidth > window.innerHeight;
    
    // Adjust container dimensions
    const containerWidth = Math.min(window.innerWidth * 0.95, 1200);
    const containerHeight = isLandscape ? 
      window.innerHeight * 0.65 :  // Increased from 0.6
      window.innerHeight * 0.55;   // Increased from 0.5

    // Set canvas dimensions
    canvasEl.width = containerWidth * ratio;
    canvasEl.height = containerHeight * ratio;
    canvasEl.style.width = `${containerWidth}px`;
    canvasEl.style.height = `${containerHeight}px`;

    // Scale context
    this.ctx.scale(ratio, ratio);
    
    // Redraw letter with new dimensions
    this.drawLetter();
  }

  drawLetter(): void {
    if (!this.ctx || !this.font || !this.selectedFont) {
      return;
    }

    const fontFamily = this.selectedFont.fontFamily;
    const canvasEl = this.canvas.nativeElement;
    const ctx = this.ctx;
    
    // Clear the canvas
    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

    // Create a temporary canvas for letter detection
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvasEl.width;
    tempCanvas.height = canvasEl.height;
    const tempCtx = tempCanvas.getContext('2d')!;

    // Fill temporary canvas with white background
    tempCtx.fillStyle = 'white';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempCtx.fillStyle = 'black';  // Set fill color to black for letter detection

    const ratio = window.devicePixelRatio || 1;
    const displayWidth = parseInt(canvasEl.style.width.replace('px', ''), 10);
    const displayHeight = parseInt(canvasEl.style.height.replace('px', ''), 10);

    // Calculate target size based on orientation and device
    const isLandscape = window.innerWidth > window.innerHeight;
    
    // Adjust size multipliers based on letter complexity
    const sizeMultiplier = this.getLetterSizeMultiplier(this.letter);
    
    const maxWidth = isLandscape ? 
      displayWidth * (0.75 * sizeMultiplier) : 
      displayWidth * (0.85 * sizeMultiplier);
    
    const maxHeight = isLandscape ? 
      displayHeight * (0.8 * sizeMultiplier) : 
      displayHeight * (0.75 * sizeMultiplier);

    // Initial font size calculation with adaptive sizing
    let fontSize = Math.min(maxWidth, maxHeight) * 0.65 * sizeMultiplier;
    let attempts = 0;
    const maxAttempts = 25; // Increased for more precise sizing
    let bestFit = fontSize;
    let minSize = fontSize * 0.2;
    let maxSize = fontSize * 2.2;

    // Binary search with improved bounds checking
    while (attempts < maxAttempts) {
      tempCtx.font = `${fontSize}px "${fontFamily}"`;
      const metrics = tempCtx.measureText(this.letter);
      
      const actualHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
      const actualWidth = metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight;

      // Add padding factor for better spacing
      const paddingFactor = 1.1; // 10% padding
      const adjustedWidth = actualWidth * paddingFactor;
      const adjustedHeight = actualHeight * paddingFactor;

      const widthRatio = maxWidth / adjustedWidth;
      const heightRatio = maxHeight / adjustedHeight;
      const scaleFactor = Math.min(widthRatio, heightRatio);

      // Adaptive bounds based on letter complexity
      const lowerBound = 0.92 * sizeMultiplier;
      const upperBound = 0.98 * sizeMultiplier;

      if (scaleFactor > lowerBound && scaleFactor < upperBound) {
        bestFit = fontSize;
        break;
      }

      if (scaleFactor > 1) {
        minSize = fontSize;
        fontSize = (fontSize + maxSize) / 2;
      } else {
        maxSize = fontSize;
        fontSize = (fontSize + minSize) / 2;
      }

      attempts++;
      bestFit = fontSize;
    }

    // Apply final size with adaptive reduction
    const finalFontSize = Math.floor(bestFit * (0.98 * sizeMultiplier));
    const fontString = `${finalFontSize}px "${fontFamily}"`;
    
    tempCtx.font = fontString;
    ctx.font = fontString;

    // Center alignment
    tempCtx.textAlign = 'center';
    tempCtx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const centerX = canvasEl.width / (2 * ratio);
    const centerY = canvasEl.height / (2 * ratio);

    // Before drawing on the main canvas, draw the letter in black on the temp canvas
    tempCtx.font = fontString;
    tempCtx.textAlign = 'center';
    tempCtx.textBaseline = 'middle';
    tempCtx.fillText(this.letter, centerX, centerY);

    // Process the pixels for letter detection
    this.processLetterPixels(tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height));

    // Now draw the letter on the main canvas with the desired style
    if (this.isDarkMode()) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    } else {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    }

    ctx.lineWidth = Math.max(3, finalFontSize * 0.02 * sizeMultiplier);
    ctx.fillText(this.letter, centerX, centerY);
    ctx.strokeText(this.letter, centerX, centerY);
  }

  private processLetterPixels(imageData: ImageData): void {
    const pixels = imageData.data;
    this.letterPixels = [];
    
    for (let y = 0; y < imageData.height; y++) {
      this.letterPixels[y] = [];
      for (let x = 0; x < imageData.width; x++) {
        const i = (y * imageData.width + x) * 4;
        // Check for black pixels (letter) vs white background
        // Using a more lenient threshold for better detection
        const isLetterPixel = pixels[i] < 128 && pixels[i + 1] < 128 && pixels[i + 2] < 128;
        this.letterPixels[y][x] = isLetterPixel;
      }
    }
  }

  private drawLetterFallback(): void {
    const canvasEl = this.canvas.nativeElement;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    
    const ratio = window.devicePixelRatio || 1;
    const canvasWidth = canvasEl.width / ratio;
    const canvasHeight = canvasEl.height / ratio;

    // Set colors based on theme
    if (this.isDarkMode()) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    } else {
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    }

    // Draw a simple rectangle as a fallback
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    ctx.strokeRect(0, 0, canvasWidth, canvasHeight);
  }

  addEventListeners(canvasEl: HTMLCanvasElement) {
    const preventDefaultTouch = (event: TouchEvent) => {
      event.preventDefault();
      if (event.touches.length > 1) {
        // Prevent multitouch gestures
        this.drawing = false;
      }
    };

    // Optimize touch events
    canvasEl.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.startDrawing(e);
    }, { passive: false });
    
    canvasEl.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        this.draw(e);
      }
    }, { passive: false });
    
    // Existing mouse events
    canvasEl.addEventListener('mousedown', this.startDrawing.bind(this));
    canvasEl.addEventListener('mousemove', this.draw.bind(this));
    canvasEl.addEventListener('mouseup', this.stopDrawing.bind(this));
    canvasEl.addEventListener('mouseout', this.stopDrawing.bind(this));
    canvasEl.addEventListener('touchend', this.stopDrawing.bind(this));
    
    // Prevent unwanted touch behaviors
    canvasEl.addEventListener('touchstart', preventDefaultTouch, { passive: false });
    canvasEl.addEventListener('touchmove', preventDefaultTouch, { passive: false });
  }

  getMousePosition(event: MouseEvent | TouchEvent): { x: number, y: number } {
    const canvasEl = this.canvas.nativeElement;
    const rect = canvasEl.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    
    // Get coordinates based on event type
    let clientX = 'clientX' in event ? event.clientX : event.touches[0].clientX;
    let clientY = 'clientY' in event ? event.clientY : event.touches[0].clientY;
    
    // Calculate precise position considering device pixel ratio and scroll
    const x = (clientX - rect.left) * (canvasEl.width / (rect.width * ratio));
    const y = (clientY - rect.top) * (canvasEl.height / (rect.height * ratio));
    
    return { x, y };
  }

  startDrawing(event: MouseEvent | TouchEvent) {
    event.preventDefault();
    const { x, y } = this.getMousePosition(event);
    const pixelX = Math.floor(x);
    const pixelY = Math.floor(y);
    
    // Only start drawing if we're within the letter boundaries
    if (this.isPointInLetter(pixelX, pixelY)) {
      this.drawing = true;
      this.lastX = pixelX;
      this.lastY = pixelY;
      
      this.ctx.beginPath();
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.shadowBlur = 1;
      this.ctx.shadowColor = '#FF0000';
      this.ctx.lineWidth = this.getDeviceSpecificLineWidth(
        window.innerWidth <= 768 ? 'mobile' : 
        window.innerWidth <= 1024 ? 'tablet' : 'desktop'
      );
      this.ctx.strokeStyle = '#FF0000';
      
      this.ctx.moveTo(pixelX, pixelY);
      this.ctx.lineTo(pixelX, pixelY);
      this.ctx.stroke();
    }
  }

  draw(event: MouseEvent | TouchEvent) {
    if (!this.drawing || !this.letterPixels.length) return;
    event.preventDefault();
    
    const { x, y } = this.getMousePosition(event);
    const pixelX = Math.floor(x);
    const pixelY = Math.floor(y);
    
    if (this.isStrictlyWithinLetter(pixelX, pixelY)) {
      this.ctx.save();
      
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.shadowBlur = 1;
      this.ctx.shadowColor = '#FF0000';
      
      if (window.innerWidth <= 768) {
        this.ctx.lineWidth = this.getDeviceSpecificLineWidth('mobile');
      } else if (window.innerWidth <= 1024) {
        this.ctx.lineWidth = this.getDeviceSpecificLineWidth('tablet');
      } else {
        this.ctx.lineWidth = this.getDeviceSpecificLineWidth('desktop');
      }
      
      this.ctx.strokeStyle = '#FF0000';
      
      if (this.isStrictlyWithinLetter(this.lastX, this.lastY)) {
        this.ctx.beginPath();
        const midX = (this.lastX + pixelX) / 2;
        const midY = (this.lastY + pixelY) / 2;
        this.ctx.moveTo(this.lastX, this.lastY);
        this.ctx.quadraticCurveTo(midX, midY, pixelX, pixelY);
        this.ctx.stroke();
      } else {
        this.ctx.beginPath();
        this.ctx.moveTo(pixelX, pixelY);
        this.ctx.lineTo(pixelX, pixelY);
        this.ctx.stroke();
      }
      
      this.ctx.restore();
      this.lastX = pixelX;
      this.lastY = pixelY;
    } else {
      this.drawing = false;
    }
  }

  stopDrawing() {
    this.drawing = false;
  }

  clearCanvas() {
    this.letterBoundingBox = null;
    this.letterPath = null;
    this.romanTransliteration = this.transliterateToRoman(this.letter);
    this.drawLetter();
  }

  generateUyirMeiLetters(mei: string): string[] {
    return this.uyirSigns.map(sign => mei.slice(0, -1) + sign);
  }

  selectLetter(letter: string) {
    if (this.meiletters.includes(letter)) {
      this.selectedMei = letter;
      this.dynamicUyirMeiLetters = this.generateUyirMeiLetters(letter);
      this.letter = letter;
    } else if (this.uyirletters.includes(letter) && this.selectedMei) {
      this.letter = this.combineLetters(this.selectedMei, letter);
      this.selectedMei = '';
    } else {
      this.letter = letter;
    }
    this.clearCanvas();
    this.playAudioForChar(letter);
    this.romanTransliteration = this.transliterateToRoman(this.letter);
  }

  combineLetters(mei: string, uyir: string): string {
    const uyirIndex = this.uyirletters.indexOf(uyir);
    if (uyirIndex > -1) {
      return mei.slice(0, -1) + this.uyirSigns[uyirIndex];
    }
    return mei;
  }

  playAudioForChar(char: string) {
    let audio = new Audio(`assets/audio/${char}.mp3`);
    audio.play().catch(error => console.error('Error playing audio:', error));
  }

  resetSelection() {
    this.selectedMei = '';
    this.dynamicUyirMeiLetters = [];
    this.romanTransliteration = '';
    this.clearCanvas();
  }

  private getLetterPixels(): void {
    const ctx = this.ctx;
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const pixels = imageData.data;
    
    this.letterPixels = [];
    for (let y = 0; y < ctx.canvas.height; y++) {
      this.letterPixels[y] = [];
      for (let x = 0; x < ctx.canvas.width; x++) {
        const i = (y * ctx.canvas.width + x) * 4;
        // More strict alpha threshold for better letter boundary detection
        this.letterPixels[y][x] = pixels[i + 3] > 100; // Increased threshold
      }
    }
    this.letterImageData = imageData;
  }

  private isPointInLetter(x: number, y: number): boolean {
    const pixelX = Math.floor(x);
    const pixelY = Math.floor(y);
    
    return pixelX >= 0 && 
           pixelX < this.letterPixels[0]?.length && 
           pixelY >= 0 && 
           pixelY < this.letterPixels.length && 
           this.letterPixels[pixelY][pixelX];
  }

  private isPointInLetterWithMargin(x: number, y: number): boolean {
    const pixelX = Math.floor(x);
    const pixelY = Math.floor(y);
    
    // Check the point itself and surrounding pixels
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const checkX = pixelX + dx;
        const checkY = pixelY + dy;
        
        if (checkX >= 0 && 
            checkX < this.letterPixels[0]?.length && 
            checkY >= 0 && 
            checkY < this.letterPixels.length) {
          if (this.letterPixels[checkY][checkX]) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private isStrictlyWithinLetter(x: number, y: number): boolean {
    const pixelX = Math.floor(x);
    const pixelY = Math.floor(y);
    
    if (pixelX < 0 || pixelX >= this.letterPixels[0]?.length || 
        pixelY < 0 || pixelY >= this.letterPixels.length) {
      return false;
    }
    
    if (!this.letterPixels[pixelY][pixelX]) {
      return false;
    }
    
    let adjacentLetterPixels = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        
        const checkX = pixelX + dx;
        const checkY = pixelY + dy;
        
        if (checkX >= 0 && checkX < this.letterPixels[0].length &&
            checkY >= 0 && checkY < this.letterPixels.length &&
            this.letterPixels[checkY][checkX]) {
          adjacentLetterPixels++;
        }
      }
    }
    
    return adjacentLetterPixels >= 2;
  }

  private calculateFontSize(width: number, height: number): number {
    const ratio = window.devicePixelRatio || 1;
    const minDimension = Math.min(width, height);
    
    // Base size calculation with larger initial size
    let fontSize = minDimension * 1.6; // Increased from 0.8 to 1.6
    
    // Adjust for device type with larger multipliers
    if (window.innerWidth <= 768) { // Mobile
      fontSize *= 1.5; // Increased from 0.8 to 1.5
    } else if (window.innerWidth <= 1024) { // Tablet
      fontSize *= 1.2; // Increased from 0.7 to 1.2
    } else { // Desktop
      fontSize *= 1.0; // Increased from 0.6 to 1.0
    }
    
    // Adjust minimum and maximum sizes
    fontSize = Math.max(fontSize, 100); // Increased minimum size
    fontSize = Math.min(fontSize, 800); // Increased maximum size
    
    return fontSize / ratio;
  }

  private transliterateToRoman(tamilText: string): string {
    // Mapping for individual characters
    const tamilToRomanMap: { [key: string]: string } = {
      'அ': 'a', 'ஆ': 'ā', 'இ': 'i', 'ஈ': 'ī', 
      'உ': 'u', 'ஊ': 'ū', 'எ': 'e', 'ஏ': 'ē', 
      'ஐ': 'ai', 'ஒ': 'o', 'ஓ': 'ō', 'ஔ': 'au', 
      // Mei without inherent 'a' sound
      'க்': 'k', 'ங்': 'ṅg', 'ச்': 'c', 'ஞ்': 'ñj', 
      'ட்': 'd', 'ண்': 'ṇ', 'த்': 't', 'ந்': 'n', 
      'ப்': 'p', 'ம்': 'm', 'ய்': 'y', 'ர்': 'r', 
      'ல்': 'l', 'வ்': 'v', 'ழ்': 'ḻh', 'ள்': 'ḷ', 
      'ற்': 'ṟ', 'ன்': 'ṉ',
      // Mei with inherent 'a' sound
      'க': 'ka', 'ங': 'ṅga', 'ச': 'ca', 'ஞ': 'ñja', 
      'ட': 'da', 'ண': 'ṇa', 'த': 'ta', 'ந': 'na', 
      'ப': 'pa', 'ம': 'ma', 'ய': 'ya', 'ர': 'ra', 
      'ல': 'la', 'வ': 'va', 'ழ': 'ḻha', 'ள': 'ḷa', 
      'ற': 'ṟa', 'ன': 'ṉa',
    };

    // Mapping for uyir markers
    const uyirMarkerMap: { [key: string]: string } = {
      'ா': 'ā', 'ி': 'i', 'ீ': 'ī', 'ு': 'u', 'ூ': 'ū',
      'ெ': 'e', 'ே': 'ē', 'ை': 'ai', 'ொ': 'o', 'ோ': 'ō', 'ௌ': 'au',
      '்': '', // Pulli, used to suppress the inherent uyir
    };

    let transliteratedText = '';
    let i = 0;

    while (i < tamilText.length) {
      const currentChar = tamilText[i];
      const nextChar = tamilText[i + 1];

      if (uyirMarkerMap[nextChar] !== undefined) {
        // Combine consonant with uyir marker
        const combined = tamilToRomanMap[currentChar + '்'] + uyirMarkerMap[nextChar];
        transliteratedText += combined || currentChar + nextChar;
        i += 2; // Skip the next character as it's a uyir marker
      } else {
        // Single character, just transliterate
        transliteratedText += tamilToRomanMap[currentChar] || currentChar;
        i++;
      }
    }

    return transliteratedText;
  }

  // Add new helper method for device-specific line widths
  private getDeviceSpecificLineWidth(deviceType: 'mobile' | 'tablet' | 'desktop'): number {
    const pixelRatio = window.devicePixelRatio || 1;
    
    switch (deviceType) {
      case 'mobile':
        // Reduced thickness for mobile touch screens
        return Math.max(6 * pixelRatio, 8);
      case 'tablet':
        // Reduced thickness for tablets
        return Math.max(8 * pixelRatio, 10);
      case 'desktop':
        // Reduced thickness for mouse control
        return Math.max(8 * pixelRatio, 10);
      default:
        return 6;
    }
  }

  showWriteOrder(character: string) {
    this.currentCharacter = character;
    this.WriteOrderVisible = true;
    this.videoLoaded = false;
    
    if (this.selectedDemoType === 'video') {
      // Try to play immediately
      this.playVideo();
      
      // Backup attempt with shorter timeout
      setTimeout(() => {
        this.playVideo();
      }, 100);
    }
  }

  hideWriteOrder() {
    this.WriteOrderVisible = false;
    if (this.strokeVideo?.nativeElement) {
      const video = this.strokeVideo.nativeElement;
      video.pause();
      video.currentTime = 0;
      video.muted = false; // Reset muted state
    }
  }

  getWriteOrderImage(character: string): string {
    // Using the existing images folder
    return `assets/images/${character}.png`;
  }

  getWriteOrderVideo(character: string): string {
    // First try to load the MP4 version
    const mp4Path = `assets/video/${character}.mp4`;
    
    // If you have WebM versions of your videos, you can use this as fallback
    // const webmPath = `assets/video/${character}.webm`;
    
    // For now, return MP4 path
    return mp4Path;
  }

  onVideoLoaded() {
    this.videoLoaded = true;
    // Try to play as soon as video is loaded
    this.playVideo();
  }

  onVideoError() {
    this.videoLoaded = false;
  }

  // Add a method to detect iOS devices
  private isIOS(): boolean {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
  }

  // New method to handle video playback
  private playVideo() {
    if (this.strokeVideo?.nativeElement) {
      const video = this.strokeVideo.nativeElement;
      
      // Set video attributes
      video.playsInline = true;
      video.muted = false; // Enable audio
      video.controls = false;
      video.currentTime = 0;
      video.loop = true;
      video.autoplay = true;
      video.preload = 'auto';
      video.volume = 1.0; // Ensure volume is up
      
      // Handle iOS specific setup
      if (this.isIOS()) {
        video.setAttribute('webkit-playsinline', 'true');
      }

      // Immediate play attempt with sound
      const playAttempt = async () => {
        try {
          // First try to play with sound
          video.muted = false;
          await video.play();
        } catch (err) {
          // If failed, try muted first then unmute
          try {
            video.muted = true;
            await video.play();
            // Once playing, unmute
            video.muted = false;
          } catch (error) {
          }
        }
      };

      playAttempt();
    }
  }

  // Add this method to load fonts
  private async loadAvailableFonts() {
    this.fontService.getFontList().subscribe({
      next: (fonts) => {
        this.availableFonts = fonts;
        
        // Categorize fonts
        this.categorizedFonts[0].fonts = fonts.filter(font => 
          !['TAMILI', 'VATTELUTTU', 'OLDFORM'].includes(font.name)
        );
        
        this.categorizedFonts[1].fonts = fonts.filter(font => 
          ['TAMILI', 'VATTELUTTU', 'OLDFORM'].includes(font.name)
        );
        
        if (fonts.length > 0) {
          this.selectedFont = fonts[0];
          this.loadFont();
        }
      },
      error: (err) => {
        const fallbackFont: FontDetails = {
          name: 'KURALI',
          displayName: 'Kurali',
          displayNameKey: 'FONT.KURALI',
          path: 'assets/font/KURALI.TTF',
          fontFamily: 'Kurali'
        };
        this.availableFonts = [fallbackFont];
        this.categorizedFonts[0].fonts = [fallbackFont];
        this.selectedFont = fallbackFont;
      }
    });
  }

  // Add font change handler
  async onFontChange(event: any) {
    const font: FontDetails = event.detail.value;
    try {
      this.selectedFont = font;
      this.font = null;
      await this.loadFont();
    } catch (error: any) {
      console.error('Font change error:', error);
    }
  }

  private loadFontStyle(fontPath: string, fontFamily: string) {
    
    // Remove any existing style for this font
    const existingStyle = document.getElementById(`font-face-${fontFamily}`);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create new style element
    const style = document.createElement('style');
    style.id = `font-face-${fontFamily}`;
    style.textContent = `
      @font-face {
        font-family: "${fontFamily}";
        src: url("${fontPath}") format("truetype");
        font-weight: normal;
        font-style: normal;
        font-display: block;
      }
    `;
    document.head.appendChild(style);
    
    // Force a browser repaint
    document.body.style.visibility = 'hidden';
    document.body.offsetHeight;
    document.body.style.visibility = 'visible';
  }

  toggleFontSelect(event: Event) {
    event.stopPropagation();
    this.showFontSelect = !this.showFontSelect;
    // Close the dropdown when clicking outside
    if (this.showFontSelect) {
      setTimeout(() => {
        document.addEventListener('click', this.closeFontSelect);
      });
    }
  }

  closeFontSelect = () => {
    this.showFontSelect = false;
    document.removeEventListener('click', this.closeFontSelect);
  }

  selectFont(font: any) {
    this.selectedFont = font;
    this.onFontChange({ detail: { value: font } });
    this.showFontSelect = false;
  }

  // Clean up event listener when component is destroyed
  ngOnDestroy() {
    document.removeEventListener('click', this.closeFontSelect);
  }

  get currentFontName(): string {
    return this.selectedFont?.displayNameKey || 'FONT.DEFAULT';
  }

  // Add this new helper method to determine size multiplier based on letter complexity
  private getLetterSizeMultiplier(letter: string): number {
    // Base multiplier
    let multiplier = 1.0;

    // Reduce size for compound letters (uyirmei)
    if (letter.length > 1) {
      multiplier *= 0.9;
    }

    // Further reduce size for specific complex combinations
    if (letter.includes('ை') || letter.includes('ௌ')) {
      multiplier *= 0.85; // More reduction for letters with complex modifiers
    }

    // Adjust for specific letter groups
    const tallLetters = ['ஈ', 'ஊ', 'ஐ', 'ஔ'];
    if (tallLetters.includes(letter)) {
      multiplier *= 0.9;
    }

    // Adjust for letters with ascending/descending parts
    const wideLetters = ['ஜ', 'ஶ', 'க்ஷ'];
    if (wideLetters.some(l => letter.includes(l))) {
      multiplier *= 0.85;
    }

    // Ensure multiplier stays within reasonable bounds
    return Math.max(0.7, Math.min(multiplier, 1.0));
  }
}